// src/pages/auth/VerifyEmail.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  verifyEmail, 
  resendVerification,
  clearError,
  selectAuthLoading,
  selectAuthError
} from '../../store/slices/authSlice';
import AuthLayout from './AuthLayout';
import {
  Mail,
  CheckCircle,
  XCircle,
  LoaderCircle,
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  Clock
} from 'lucide-react';

const VerifyEmail = ({ showToast }) => {
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [countdown, setCountdown] = useState(0);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    const emailParam = params.get('email');

    if (tokenParam && emailParam) {
      setToken(tokenParam);
      setEmail(emailParam);
      handleVerifyEmail(tokenParam, emailParam);
    } else {
      // Check if coming from registration
      const state = location.state;
      if (state && state.email) {
        setEmail(state.email);
        setVerificationError('Please check your email for the verification link.');
        setIsVerifying(false);
      } else {
        setVerificationError('Invalid verification link. Please check your email.');
        setIsVerifying(false);
      }
    }
  }, [location]);

  const handleVerifyEmail = async (token, email) => {
    setIsVerifying(true);
    setVerificationError('');
    
    try {
      const result = await dispatch(verifyEmail({ token, email })).unwrap();
      
      if (result.success) {
        setIsVerified(true);
        if (showToast) {
          showToast('✅ Email verified successfully!', 'success');
        }
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { message: 'Email verified successfully. You can now login.' }
          });
        }, 3000);
      } else {
        setIsVerified(false);
        setVerificationError(result.message || 'Failed to verify email');
      }
    } catch (err) {
      setIsVerified(false);
      
      if (err.message) {
        // Check if expired and new token was sent
        if (err.message.toLowerCase().includes('expired')) {
          setVerificationError('Your verification link has expired. A new one has been sent to your email.');
        } else {
          setVerificationError(err.message);
        }
      }
      
      if (showToast) {
        showToast(err.message || 'Failed to verify email', 'error');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendVerification = async () => {
    if (isResending || countdown > 0) return;
    
    setIsResending(true);
    setResendError('');
    setResendSuccess(false);
    
    try {
      const result = await dispatch(resendVerification({ email })).unwrap();
      
      if (result.success) {
        setResendSuccess(true);
        setCountdown(60); // 60 seconds countdown
        if (showToast) {
          showToast('✅ Verification email resent successfully!', 'success');
        }
      } else {
        setResendError(result.message || 'Failed to resend verification email');
      }
    } catch (err) {
      setResendError(err.message || 'Failed to resend verification email');
      if (showToast) {
        showToast(err.message || 'Failed to resend verification email', 'error');
      }
    } finally {
      setIsResending(false);
    }
  };

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Loading state
  if (isVerifying) {
    return (
      <AuthLayout title="Verifying Email" subtitle="Please wait while we verify your email">
        <div style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center', padding: '40px 0' }}>
          <LoaderCircle size={48} style={{ animation: 'spin 1s linear infinite', color: '#d4af37' }} />
          <p style={{ marginTop: '20px', color: '#64748b', fontSize: '16px' }}>
            Verifying your email address...
          </p>
          <p style={{ marginTop: '8px', color: '#94a3b8', fontSize: '14px' }}>
            This should only take a moment.
          </p>
        </div>
      </AuthLayout>
    );
  }

  // Verified state
  if (isVerified) {
    return (
      <AuthLayout title="Email Verified!" subtitle="Your email has been successfully verified">
        <div style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center', padding: '20px 0' }}>
          <div style={{ 
            marginBottom: '24px',
            padding: '40px',
            backgroundColor: '#d1fae5',
            borderRadius: '50%',
            width: '100px',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <CheckCircle size={60} color="#065f46" />
          </div>
          
          <h2 style={{ color: '#1a1a2e', marginBottom: '12px' }}>
            Email Verified Successfully!
          </h2>
          
          <p style={{ color: '#64748b', fontSize: '15px', marginBottom: '24px' }}>
            Your email has been verified. You can now log in to your account.
          </p>
          
          <Link 
            to="/login"
            style={{
              display: 'block',
              width: '100%',
              padding: '18px',
              fontSize: '18px',
              fontWeight: '700',
              borderRadius: '10px',
              backgroundColor: '#d4af37',
              color: '#1a1a2e',
              border: 'none',
              textAlign: 'center',
              textDecoration: 'none',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#c5a032'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#d4af37'}
          >
            Proceed to Login
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Error state
  return (
    <AuthLayout title="Verify Email" subtitle="Please verify your email address">
      <div style={{ maxWidth: '520px', margin: '0 auto' }}>
        {verificationError && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px 20px', 
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#fee2e2',
            color: '#dc2626',
            border: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <XCircle size={20} />
            <span>{verificationError}</span>
          </div>
        )}

        {resendSuccess && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px 20px', 
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            border: '1px solid #a7f3d0',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <CheckCircle size={20} />
            <span>Verification email sent successfully! Please check your inbox.</span>
          </div>
        )}

        {resendError && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px 20px', 
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#fee2e2',
            color: '#dc2626',
            border: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <XCircle size={20} />
            <span>{resendError}</span>
          </div>
        )}

        <div style={{ 
          marginBottom: '24px', 
          padding: '20px',
          borderRadius: '10px',
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <Mail size={24} color="#d4af37" />
            <div>
              <strong style={{ display: 'block', color: '#1a1a2e' }}>Verification Email</strong>
              <span style={{ color: '#64748b', fontSize: '14px' }}>{email}</span>
            </div>
          </div>
          
          <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>
            We've sent a verification link to your email address. 
            Please check your inbox and click the link to verify your account.
          </p>
          
          <div style={{ 
            padding: '12px',
            backgroundColor: '#fef3c7',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertTriangle size={16} />
            <span>
              <strong>Didn't receive the email?</strong> Check your spam folder or request a new link below.
            </span>
          </div>
        </div>

        <button
          onClick={handleResendVerification}
          disabled={isResending || countdown > 0}
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
            borderRadius: '10px',
            backgroundColor: isResending || countdown > 0 ? '#e2e8f0' : '#d4af37',
            color: isResending || countdown > 0 ? '#94a3b8' : '#1a1a2e',
            border: 'none',
            cursor: isResending || countdown > 0 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}
          onMouseEnter={(e) => {
            if (!isResending && countdown === 0) {
              e.target.style.backgroundColor = '#c5a032';
            }
          }}
          onMouseLeave={(e) => {
            if (!isResending && countdown === 0) {
              e.target.style.backgroundColor = '#d4af37';
            }
          }}
        >
          {isResending ? (
            <>
              <LoaderCircle size={20} style={{ animation: 'spin 1s linear infinite' }} />
              Sending...
            </>
          ) : countdown > 0 ? (
            <>
              <Clock size={20} />
              Resend available in {countdown}s
            </>
          ) : (
            <>
              <RefreshCw size={20} />
              Resend Verification Email
            </>
          )}
        </button>

        <div style={{ 
          textAlign: 'center', 
          marginTop: '24px'
        }}>
          <Link to="/login" style={{ 
            color: '#64748b',
            textDecoration: 'none',
            fontSize: '15px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.color = '#d4af37'}
          onMouseLeave={(e) => e.target.style.color = '#64748b'}
          >
            <ArrowLeft size={18} /> Back to Sign In
          </Link>
        </div>
      </div>

      {/* Add spin animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </AuthLayout>
  );
};

export default VerifyEmail;